import { memo, useCallback, useMemo } from 'react';
import { Pagination as BSPagination } from 'react-bootstrap';

interface PaginationProps {
  readonly currentPage: number;
  readonly totalItems: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly disabled?: boolean;
}

export const Pagination = memo<PaginationProps>(function Pagination({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  disabled,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }, [currentPage, totalPages]);

  const handleFirst = useCallback(() => onPageChange(1), [onPageChange]);
  const handlePrev = useCallback(
    () => onPageChange(currentPage - 1),
    [currentPage, onPageChange]
  );
  const handleNext = useCallback(
    () => onPageChange(currentPage + 1),
    [currentPage, onPageChange]
  );
  const handleLast = useCallback(
    () => onPageChange(totalPages),
    [onPageChange, totalPages]
  );

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="d-flex justify-content-between align-items-center">
      <span className="text-muted">
        Showing {(currentPage - 1) * pageSize + 1} -{' '}
        {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
      </span>
      <BSPagination className="mb-0">
        <BSPagination.First
          onClick={handleFirst}
          disabled={disabled || currentPage === 1}
        />
        <BSPagination.Prev
          onClick={handlePrev}
          disabled={disabled || currentPage === 1}
        />
        {pageNumbers[0] > 1 && <BSPagination.Ellipsis disabled />}
        {pageNumbers.map((page) => (
          <BSPagination.Item
            key={page}
            active={page === currentPage}
            onClick={() => onPageChange(page)}
            disabled={disabled}
          >
            {page}
          </BSPagination.Item>
        ))}
        {pageNumbers[pageNumbers.length - 1] < totalPages && (
          <BSPagination.Ellipsis disabled />
        )}
        <BSPagination.Next
          onClick={handleNext}
          disabled={disabled || currentPage === totalPages}
        />
        <BSPagination.Last
          onClick={handleLast}
          disabled={disabled || currentPage === totalPages}
        />
      </BSPagination>
    </div>
  );
});
